const KNOWN_PROTEINS = ['beef', 'chicken', 'lamb', 'kangaroo', 'salmon', 'duck', 'turkey', 'barramundi', 'goat', 'venison', 'crocodile', 'seafood', 'rabbit'];

/** @param {string} value */
const normaliseProtein = (value) => value.replace(/[\s\u00a0]+/g, ' ').trim().toLowerCase();

/** Normalised name with the word "protein" dropped, so "Chicken Protein Boost" equals "Chicken Boost".
 * @param {string} value */
const canon = (value) => normaliseProtein(value).replace(/\bprotein\b/g, '').replace(/\s+/g, ' ').trim();

/** @param {string} value */
const proteinsIn = (value) => KNOWN_PROTEINS.filter(protein => new RegExp(`\\b${protein}\\b`).test(value));

/**
 * Hide the Ingredients sections (an <h5> and everything up to the next <h5>) for
 * proteins this product doesn't sell. A heading is kept when it equals a value,
 * names no protein (e.g. a warning), or is a bare protein named inside a value that
 * matches no heading exactly. Compound headings ("Chicken Boost") need an exact value.
 * @param {HTMLElement} container element carrying data-ingredient-proteins
 */
export function filterIngredients(container) {
    const values = (container.dataset.ingredientProteins || '').split('|').map(canon).filter(Boolean);
    const headings = container.querySelectorAll('h5');
    if (!values.length || !headings.length) return;

    const headingKeys = new Set([...headings].map(heading => canon(heading.textContent || '')));
    const exact = new Set(values);
    const wordAllowed = new Set(values.filter(value => !headingKeys.has(value)).flatMap(proteinsIn));
    if (!values.some(value => headingKeys.has(value)) && !wordAllowed.size) return;

    let kept = 0;
    headings.forEach(heading => {
        const key = canon(heading.textContent || '');
        const words = proteinsIn(key);
        const keep = exact.has(key) || !words.length || (words.length === 1 && key === words[0] && wordAllowed.has(key));
        if (keep) kept++;

        /** @type {Element|null} */
        let el = heading;
        do {
            if (el instanceof HTMLElement) el.hidden = !keep;
            el = el.nextElementSibling;
        } while (el && el.tagName !== 'H5');
    });

    const accordion = container.closest('[ref="accordion"]');
    if (accordion instanceof HTMLElement) accordion.hidden = kept === 0;
}

export class ProductDetailsAccordion extends HTMLElement {
    /**@type {NodeList} */
    refs = this.querySelectorAll('[ref="accordion"]');

    #state = {
        /**@type {HTMLElement|null} */
        active: null
    }

    constructor() {
        super();
        if (!this.refs.length) {
            throw new Error("[ref='accordion'] is required");
        }
        if (typeof $ !== 'function') {
            throw new Error('jQuery is not detected at ProductDetailsAccordion');
        }
    }

    /**@type {AbortController|null} */
    #controller = null;

    close() {
        ($(this.#state.active?.querySelector('.contents')).slideUp(150), this.#state.active?.removeAttribute('open'), this.#state.active = null)
    }

    open() {
        (this.#state.active?.setAttribute('open', 'true'), $(this.#state.active?.querySelector('.contents')).slideDown(150));
    }

    #toggleActiveAccordion() {
        this.#state.active?.hasAttribute('open')
            ? this.close()
            : this.open();
    }

    /**@param {PointerEvent} event */
    #handleAccordionClick (event) {
        if ((event.target instanceof HTMLElement && event.target?.closest('[ref="accordion"]')) === this.#state.active) {
            this.close();
            return;
        }

        this.#toggleActiveAccordion();
        this.#state.active = event.target instanceof HTMLElement ? event.target?.closest('[ref="accordion"]') : null;
        this.#toggleActiveAccordion();
    }

    connectedCallback() {
        this.querySelectorAll('.contents.ingredients[data-ingredient-proteins]').forEach(container => {
            container instanceof HTMLElement && filterIngredients(container);
        });

        // A fresh controller per connection: an aborted signal would silently drop the listeners on reconnect.
        this.#controller?.abort();
        this.#controller = new AbortController();
        const { signal } = this.#controller;
        this.refs.forEach(ref => {
            if (ref instanceof HTMLElement) {
                ref.querySelector('[ref="header"]')?.addEventListener('click', this.#handleAccordionClick.bind(this), { signal })
            }
        });

        // Open the first accordion, unless one is already open from a previous connection.
        if (this.#state.active) return;
        const firstAcc = this.refs[0];
        if (firstAcc instanceof HTMLElement) {
            const accHeader = firstAcc.querySelector('[ref="header"]');
            if (accHeader instanceof HTMLElement) {
                accHeader.click();
            }
        }
    }

    disconnectedCallback() {
        this.#controller?.abort();
        this.#controller = null;
    }
}

if (!customElements.get('product-details-accordion')) {
    customElements.define('product-details-accordion', ProductDetailsAccordion);
}